import { LEITNER_INTERVAL_DAYS, MAX_BOX, MIN_BOX, RECENT_RESULTS_KEPT } from '@/constants/app';
import type { Progress } from './types';

const DAY_MS = 24 * 60 * 60 * 1000;

/** A record that has never been studied. Box 1, due immediately. */
export function freshProgress(id: string, course: string): Omit<Progress, 'updatedAt'> {
  return {
    id,
    deleted: false,
    course,
    box: MIN_BOX,
    lastSeen: null,
    dueAt: null,
    attempts: 0,
    correct: 0,
    streak: 0,
    recent: [],
  };
}

export function intervalMs(box: number): number {
  const days = LEITNER_INTERVAL_DAYS[box] ?? 0;
  return days * DAY_MS;
}

/**
 * LOCKED scheduling: a miss drops to box 1, a hit climbs one box to a maximum
 * of 5, and the next due date comes from the new box — 0, 1, 3, 7, 14 days.
 * Box 1's zero-day interval means "next session": it is due at once, and the
 * queue builder is what stops it repeating inside the run that just missed it.
 */
export function applyResult(
  current: Omit<Progress, 'updatedAt'>,
  correct: boolean,
  now: number,
): Omit<Progress, 'updatedAt'> {
  const box = correct ? Math.min(current.box + 1, MAX_BOX) : MIN_BOX;
  const recent = [...current.recent, correct].slice(-RECENT_RESULTS_KEPT);
  return {
    ...current,
    box,
    lastSeen: now,
    dueAt: now + intervalMs(box),
    attempts: current.attempts + 1,
    correct: current.correct + (correct ? 1 : 0),
    streak: correct ? current.streak + 1 : 0,
    recent,
  };
}

export function isDue(progress: Omit<Progress, 'updatedAt'> | undefined, now: number): boolean {
  if (!progress) return true; // never studied
  if (progress.dueAt === null) return true;
  return progress.dueAt <= now;
}

/**
 * Due items, lowest box first, then most overdue. Items never studied count as
 * box 1 and maximally overdue, so new material leads.
 */
export function orderQueue<T extends { id: string }>(
  items: T[],
  progressById: Map<string, Omit<Progress, 'updatedAt'>>,
  now: number,
  excludeIds: ReadonlySet<string> = new Set(),
): T[] {
  return items
    .filter((item) => !excludeIds.has(item.id))
    .filter((item) => isDue(progressById.get(item.id), now))
    .sort((a, b) => {
      const pa = progressById.get(a.id);
      const pb = progressById.get(b.id);
      const boxA = pa?.box ?? MIN_BOX;
      const boxB = pb?.box ?? MIN_BOX;
      if (boxA !== boxB) return boxA - boxB;
      const dueA = pa?.dueAt ?? 0;
      const dueB = pb?.dueAt ?? 0;
      return dueA - dueB;
    });
}

export function accuracy(progress: Pick<Progress, 'attempts' | 'correct'>): number | null {
  if (progress.attempts === 0) return null;
  return progress.correct / progress.attempts;
}
